import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { updateAvailability } from '../services/availabilityService';
import type { AvailabilityFormValues } from '../models';
import { availabilityQueryKeys } from './availabilityQueryKeys';
import { buildAvailabilityDataFromForm } from './useCreateAvailabilityMutation';

interface UpdateAvailabilityVariables {
  id: string;
  values: AvailabilityFormValues;
}

export function useUpdateAvailabilityMutation() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: UpdateAvailabilityVariables) => {
      if (!user) {
        throw new Error('Usuário não autenticado.');
      }

      if (!id) {
        throw new Error('Identificador da disponibilidade não informado.');
      }

      const availabilityData = buildAvailabilityDataFromForm(values);
      const resolvedUserName = user.displayName ?? user.email ?? 'Usuário Raro';

      await updateAvailability({
        id,
        ...availabilityData,
        updatedAt: Timestamp.now(),
        updatedById: user.uid,
        updatedByName: resolvedUserName,
        updatedByAvatar: user.photoURL ?? null,
      });

      return id;
    },
    onSuccess: (availabilityId) => {
      queryClient.invalidateQueries({ queryKey: availabilityQueryKeys.list() });
      if (availabilityId) {
        queryClient.invalidateQueries({
          queryKey: availabilityQueryKeys.detail(availabilityId),
        });
      }
    },
  });
}

export type UseUpdateAvailabilityMutationResult = ReturnType<
  typeof useUpdateAvailabilityMutation
>;

